'use client';

import { useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import { COLORS } from '@/constants/colors';
import { type CmsPost } from '@/lib/cms';

export interface CmsPostWithImageUrl extends CmsPost {
  cardImageUrl: string;
}

function PostThumbnail({ src, alt, priority }: { src: string; alt: string; priority: boolean }) {
  // CMS media occasionally 404s/500s (file missing or broken on the media
  // host). Track the failure in state - rather than mutating the <img>'s
  // style directly in the onError callback - so the fallback (the gradient
  // behind the image) only appears via a normal React re-render, never as a
  // raw DOM write racing React's hydration of this node.
  const [hasError, setHasError] = useState(false);
  if (hasError) return null;

  return (
    <CardImage
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 350px"
      priority={priority}
      // The CMS already serves pre-sized variants (card/hero), so skip
      // Vercel's image optimizer - its quota is exhausted (402s) and new
      // posts' images would otherwise never load.
      unoptimized
      onError={() => setHasError(true)}
    />
  );
}

export default function BlogListPage({ posts }: { posts: CmsPostWithImageUrl[] }) {
  const [featured, ...rest] = posts;

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <main>
        <Breadcrumbs items={[{ label: 'Blog' }]} />

        <HeroSection>
          <HeroContent>
            <HeroTitle>Blog</HeroTitle>
            <HeroSubtitle>Performance Filter Guides &amp; Expert Tips</HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <BlogSection>
          <Container>
            {posts.length === 0 ? (
              <EmptyState>No posts published yet.</EmptyState>
            ) : (
              <>
                {featured && (
                  <FeaturedCard href={`/blog/${featured.slug}`}>
                    <FeaturedImageWrapper>
                      <PostThumbnail src={featured.cardImageUrl} alt={featured.heroImage.alt || featured.title} priority />
                    </FeaturedImageWrapper>
                    <FeaturedContent>
                      <FeaturedTag>Latest Article</FeaturedTag>
                      <FeaturedTitle>{featured.title}</FeaturedTitle>
                      <FeaturedExcerpt>{featured.excerpt}</FeaturedExcerpt>
                      <CardMeta>
                        <MetaDate>
                          {new Date(featured.publishedDate).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'UTC',
                          })}
                        </MetaDate>
                        {featured.readTime && (
                          <>
                            <MetaSeparator>•</MetaSeparator>
                            <MetaReadTime>{featured.readTime}</MetaReadTime>
                          </>
                        )}
                      </CardMeta>
                      <ReadMoreLabel>Read Article →</ReadMoreLabel>
                    </FeaturedContent>
                  </FeaturedCard>
                )}

                {rest.length > 0 && (
                  <BlogGrid>
                    {rest.map((post) => (
                      <BlogCard key={post.id}>
                        <CardImageWrapper href={`/blog/${post.slug}`}>
                          <PostThumbnail src={post.cardImageUrl} alt={post.heroImage.alt || post.title} priority={false} />
                        </CardImageWrapper>
                        <CardContent>
                          <CardMeta>
                            <MetaDate>
                              {new Date(post.publishedDate).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                                timeZone: 'UTC',
                              })}
                            </MetaDate>
                            {post.readTime && (
                              <>
                                <MetaSeparator>•</MetaSeparator>
                                <MetaReadTime>{post.readTime}</MetaReadTime>
                              </>
                            )}
                          </CardMeta>
                          <CardTitle>
                            <TitleLink href={`/blog/${post.slug}`}>{post.title}</TitleLink>
                          </CardTitle>
                          <CardExcerpt>{post.excerpt}</CardExcerpt>
                          <ReadMoreButton href={`/blog/${post.slug}`}>Read Article →</ReadMoreButton>
                        </CardContent>
                      </BlogCard>
                    ))}
                  </BlogGrid>
                )}
              </>
            )}
          </Container>
        </BlogSection>
      </main>

      <Footer />
    </>
  );
}

// Styled Components
const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 80px 24px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 767px) {
    padding: 40px 20px;
  }
`;

const HeroContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #555;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const BlogSection = styled.section`
  background: ${COLORS.bgGray};
  padding: 64px 24px 100px;

  @media (max-width: 767px) {
    padding: 48px 20px 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const EmptyState = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  color: #666;
  text-align: center;
  padding: 40px 0;
`;

// A single larger "latest article" card above the grid, so the list page
// doesn't open on a wall of same-sized cards with nothing anchoring it.
const FeaturedCard = styled(Link)`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 0;
  background: ${COLORS.white};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 48px;
  text-decoration: none;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImageWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 280px;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
`;

const FeaturedContent = styled.div`
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 767px) {
    padding: 28px 24px;
  }
`;

const FeaturedTag = styled.span`
  display: inline-block;
  align-self: flex-start;
  background: ${COLORS.brandGreen};
  color: ${COLORS.white};
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 16px;
`;

const FeaturedTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.25;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const FeaturedExcerpt = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 18px;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 32px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BlogCard = styled.article`
  background: ${COLORS.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const CardImageWrapper = styled(Link)`
  position: relative;
  display: block;
  width: 100%;
  padding-bottom: 56.25%;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
  overflow: hidden;
`;

const CardImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
`;

const CardContent = styled.div`
  padding: 28px 24px 32px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const MetaDate = styled.span`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #666;
`;

const MetaSeparator = styled.span`
  color: #ccc;
  font-size: 12px;
`;

const MetaReadTime = styled.span`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #666;
`;

const CardTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const TitleLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.brandGreen};
  }
`;

const CardExcerpt = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 24px;
  flex: 1;
`;

const readMoreStyles = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.brandGreen};
  text-decoration: none;
  transition: all 0.2s ease;
  align-self: flex-start;
`;

// The featured card's whole content block is already wrapped in a Link
// (FeaturedCard), so its "Read Article" label just needs to look like the
// grid cards' ReadMoreButton without being its own nested anchor.
const ReadMoreLabel = styled.span`
  ${readMoreStyles}
`;

const ReadMoreButton = styled(Link)`
  ${readMoreStyles}

  &:hover {
    color: ${COLORS.brandGreenDark};
    gap: 10px;
  }
`;
